import { Three3DScene } from 'redhotmagma-visualization/Three/Three3DScene';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import {
  ACESFilmicToneMapping,
  Box3,
  Color,
  FileLoader,
  FrontSide,
  Object3D,
  PMREMGenerator,
  PointLight,
  Vector2,
  Vector3,
  WebGLRenderer,
  CameraHelper,
} from 'three';
import { TWEEN } from 'redhotmagma-visualization/ExposedModules';
import { isMobileDevice } from 'redhotmagma-visualization/Utils/isMobileDevice';
import { EventTypes } from 'redhotmagma-visualization/Constants';
import Deferred from '@configuratorware/configurator-frontendgui/Utils/Async/Deferred';
import envMapSource from './resources/hall_edit_mr.hdr';
import { createSurroundingsBuilder } from './Surroundings/SurroundingsBuilder';

import Stats from 'three/examples/jsm/libs/stats.module.js';

const GroupNameByMainGroupIdentifier = {
  rotoform: 'front',
  steelbeltcooler: 'middle',
  dischargedevice: 'rear',
};

function fitsMaxPercentDeviation(value, baseValue, maxPercent) {
  return Math.abs(value - baseValue) <= (baseValue * maxPercent) / 100;
}

const halfPI = Math.PI * 0.5;

export class Visualization extends Three3DScene {
  cameraPosition = new Vector3(4, 1, 0);
  defaultRelativeCameraPosition = new Vector3(4, 1, 0).normalize();
  hiddenObjects = [];
  currentCameraView = null;
  relativeScreenshotPosition = new Vector3(4, 1, 0).normalize();

  surroundingsControls = {
    azimuthAngle: 0.7,
    polarAngle: 1.32,
  };

  opacityTweenGroup = new TWEEN.Group();

  constructor(...args) {
    super(...args);
    this.surroundingsBuilder = createSurroundingsBuilder(
      this.createLoader({ onError: (err) => console.error(err) })
    );
  }

  setupDefaultControls() {
    super.setupDefaultControls();
    this.controls.maxPolarAngle = Math.PI * 0.6;

    let lastDistance = 0;
    this.controls.addEventListener('change', () => {
      const polarAngle = this.controls.getPolarAngle();
      if (polarAngle > halfPI) {
        const distance = this.controls.getDistance();
        if (Math.abs(distance - lastDistance) < 0.01) {
          return;
        }
        lastDistance = distance;
        this.controls.maxPolarAngle = halfPI + Math.asin((this.controls.target.y - 0.1) / distance);
        if (polarAngle > this.controls.maxPolarAngle) {
          this.controls.update();
        }
      }
    });
  }

  createScene() {
    const scene = super.createScene();
    scene.background = new Color().set(0xe0e0e0);
    return scene;
  }

  setSize(params, rerender = true) {
    super.setSize(params, rerender);
    if (this._size) {
      const camera = this.camera;
      const fitDistance = this.getFitDistance(camera) * 1.2;
      if (this.currentCameraView === 'default') {
        camera.position.copy(
          this.controls.target
            .clone()
            .add(
              this.camera.position.clone().sub(this.controls.target).normalize().multiplyScalar(fitDistance)
            )
        );
      }
      this.maxDistance = fitDistance * 2;
      this.controls.maxDistance = this.maxDistance;
      this.cameraViews.default?.position.copy(
        this.cameraViews.default.lookat
          .clone()
          .add(
            this.cameraViews.default.position
              .clone()
              .sub(this.cameraViews.default.lookat)
              .normalize()
              .multiplyScalar(fitDistance)
          )
      );
    }
    rerender && this.controls?.update();
  }

  loadCount = 0;
  handleLoadStart() {
    if (this.loadCount === 0) {
      this.onLoadStartCustom();
    }
    this.loadCount++;
  }
  handleLoadFinish() {
    if (this.loadCount === 0) {
      return;
    }
    this.loadCount--;
    if (this.loadCount === 0) {
      this.onLoadFinishedCustom();
    }
  }
  createLoadingManager(options) {
    const loadingManager = super.createLoadingManager(options);

    loadingManager.onStart = () => {
      this.handleLoadStart();
      const { onLoad, onError } = { ...this._loadingManager, ...options };
      loadingManager.onLoad = (...args) => {
        this.handleLoadFinish();
        onLoad && onLoad(...args);
      };
      loadingManager.onError = (...args) => {
        this.handleLoadFinish(...args);
        onError && onError(...args);
      };
    };

    return loadingManager;
  }

  createLoader(options = {}) {
    const loadingManager = this.createLoadingManager(options);

    if (!this.dracoLoader) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('libs/draco/gltf/');
      this.dracoLoader = dracoLoader;
    }

    const loader = new GLTFLoader(loadingManager);
    loader.setDRACOLoader(this.dracoLoader);
    return loader;
  }

  getSceneContainerBox() {
    const box = new Box3();
    this.sceneContainer.updateMatrixWorld();
    this.sceneContainer.updateWorldMatrix(true, true);
    this.sceneContainer.traverse((object) => {
      if (object.visible && object.isMesh && object.name !== 'ground') {
        box.expandByObject(object);
      }
    });
    return box;
  }

  createRenderer() {
    const renderer = new WebGLRenderer({
      antialias: true,
    });

    const realWidth = window.innerWidth * window.devicePixelRatio;
    if (isMobileDevice() && realWidth >= 1024) {
      renderer.setPixelRatio(1);
    } else {
      renderer.setPixelRatio(window.devicePixelRatio);
    }

    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.useLegacyLights = true;

    renderer.shadowMap.enabled = false;

    const pmremGenerator = new PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    this.envMapPromise = new Promise((resolve, reject) => {
      new RGBELoader().load(
        envMapSource,
        (texture) => {
          this.envMap = pmremGenerator.fromEquirectangular(texture).texture;
          this.scene.environment = this.envMap;
          texture.dispose();
          pmremGenerator.dispose();
          resolve(this.envMap);
        },
        undefined,
        reject
      );
    });

    if (new URL(window.location.href).searchParams.has('showStats')) {
      const stats = Stats();
      document.body.appendChild(stats.dom);
      this.stats = stats;
    }

    return renderer;
  }

  createDirLight() {
    return new Object3D();
  }

  createHemiLight() {
    return new Object3D();
  }

  createGround() {
    return new Object3D();
  }

  setupGround() {
    // lint error: function cant be empty
  }

  addLightsIfNeeded() {
    return null;
  }

  createCamera() {
    const camera = super.createCamera();
    return camera;
  }

  render() {
    if (this._disableRender) {
      return;
    }
    super.render();
    this.stats && this.stats.update();
  }

  animate = (time = 0) => {
    if (!this._isAnimating) {
      window.cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
      return;
    }
    this._animationFrame = window.requestAnimationFrame(this.animate);
    if (time) {
      this.cameraTweenGroup.update(time);
      this.opacityTweenGroup.update(time);
      if (this.controls) {
        this.controls.update();
      }
      this.render();
    }
  };

  async loadSceneContainer(item) {
    const result = await super.loadSceneContainer(item);
    const { scene } = result;

    const lights = [];
    scene.traverse((obj) => obj.isLight && lights.push(obj));
    lights.forEach((light) => light.parent.remove(light));

    const componentIdentifierMatches = scene.userData.path.match(/\/components\/([^/]+)\//);
    scene.userData.componentIdentifiers = componentIdentifierMatches
      ? componentIdentifierMatches[1].split('+')
      : null;

    scene.traverse((object) => {
      if (object.material) {
        object.material.side = FrontSide;
      }
    });

    return result;
  }

  setDynamicLights() {
    if (!this.scene) {
      return;
    }
    if (this.dynamicLights) {
      this.dynamicLights.forEach((light) => this.scene.remove(light));
    }
    const box = this.getSceneContainerBox();
    const center = box.getCenter(new Vector3());

    const pointLight1 = new PointLight(0xffffff, 0.3, 10, 2);
    pointLight1.position.set(center.x, center.y + 3, box.max.z + 2);
    const pointLight2 = new PointLight(0xffffff, 0.3, 10, 2);
    pointLight2.position.set(center.x, center.y + 3, box.min.z - 2);
    const pointLight3 = new PointLight(0xffffff, 0.3, 10, 2);
    pointLight3.position.set(box.min.x - 2, center.y + 3, center.z);
    const pointLight4 = new PointLight(0xffffff, 0.3, 10, 2);
    pointLight4.position.set(box.max.x + 2, center.y + 3, center.z);

    const lights = [pointLight1, pointLight2, pointLight3, pointLight4];
    this.scene.add(...lights);
    this.dynamicLights = lights;
  }

  async loadScene(resourceList) {
    const loadDeferred = new Deferred();
    this.sceneLoaded = loadDeferred.promise;
    this._disableRender = true;
    try {
      const result = await super.loadScene(resourceList);
      this.setDynamicLights();
      this.onModelsLoaded && this.onModelsLoaded();

      // make sure no rendering happens before the envMap is loaded
      try {
        await this.envMapPromise;
      } catch (e) {
        // ignore
      }

      this._disableRender = false;
      loadDeferred.resolve();
      this.render();

      return result;
    } catch (e) {
      loadDeferred.reject(e);
    }
  }

  checkEmptyScene() {
    // skip checking
  }

  initCustomizableMaterialsIfNeeded() {
    // prevent the core function from using CanvasTexture
  }

  getFitDistance(camera = this.camera) {
    const size = this._size;
    const padding = 2;
    const maxDim = Math.max(size.x, size.z);
    return (maxDim + padding * 2) / 2 / camera.aspect / Math.tan((Math.PI * camera.fov) / 360);
  }

  fitSceneToCamera() {
    const { controls, camera } = this;
    if (!camera || !controls) {
      return;
    }
    const box = this.getSceneContainerBox();
    const fitOffset = 1.2;

    const size = box.getSize(new Vector3());
    if (
      this._size &&
      fitsMaxPercentDeviation(size.x, this._size.x, 20) &&
      fitsMaxPercentDeviation(size.z, this._size.z, 10)
    ) {
      return;
    }
    this._size = size;

    const radius = Math.max(size.x, size.z) * 0.5;
    const center = box.getCenter(new Vector3());

    if (radius < 0.001) {
      return;
    }

    const minDistance = radius;
    const fitDistance = radius / 2 / Math.atan((((camera.fov / 2) * camera.aspect) / 360) * Math.PI);
    const distance = fitOffset * fitDistance;
    const aspectFitDistance = fitOffset * Math.max(fitDistance, fitDistance / camera.aspect);
    this.aspectFitDistance = aspectFitDistance;

    const maxDistance = distance * 2;
    this.maxDistance = maxDistance;
    this.minDistance = minDistance;

    // update controls
    if (minDistance === controls.minDistance) {
      return;
    }
    controls.minDistance = minDistance;
    controls.maxDistance = maxDistance;
    camera.near = minDistance * 0.01;
    camera.far = maxDistance * 100;
    camera.position.copy(camera.position.normalize().multiplyScalar(aspectFitDistance));
    camera.updateProjectionMatrix();
    controls.target = center;
    controls.update();
  }

  animateOpacity(objects, initialValue, targetValue) {
    return new Promise((resolve) => {
      const current = { opacity: initialValue };
      new TWEEN.Tween(current, this.opacityTweenGroup)
        .to({ opacity: targetValue }, 700)
        .easing(TWEEN.Easing.Quadratic.Out)
        .onUpdate(() => {
          this.setOpacity(objects, current.opacity);
        })
        .onComplete(resolve)
        .delay(targetValue ? 0 : 700) // hiding should be delayed to make it more visible
        .start();
    });
  }

  setOpacity(objects, value) {
    for (const object of objects) {
      object.traverse((child) => {
        if (child.material) {
          child.material.transparent = value !== 1;
          child.material.opacity = value;
          child.material.needsUpdate = true;
          if (child.material.original) {
            child.material.original.transparent = value !== 1;
            child.material.original.opacity = value;
            child.material.original.needsUpdate = true;
          }
        }
      });
    }
  }

  async getComponentsToHide(componentIdentifiers) {
    if (!componentIdentifiers) {
      return [];
    }
    try {
      const itemParams = await this.itemParamsPromise;
      for (const componentIdentifier of componentIdentifiers) {
        const components = itemParams?.hideComponents?.[componentIdentifier];
        if (components) {
          return components;
        }
      }
    } catch (e) {
      console.error('Item params not available!', e);
    }
    return [];
  }

  componentIdentifierIncludesAnyComponent(componentIdentifiers, components) {
    return componentIdentifiers?.some((identifier) => components?.includes(identifier));
  }

  updateHiddenObjects(componentsToHide, componentsToShow = []) {
    const promises = [];
    for (const scene of this.sceneContainer.children) {
      if (
        this.componentIdentifierIncludesAnyComponent(scene.userData.componentIdentifiers, componentsToHide)
      ) {
        if (!scene.userData.isHidden) {
          scene.userData.isHidden = true;
          promises.push(this.animateOpacity(scene.children, 1, 0.2));
        }
      } else if (
        this.componentIdentifierIncludesAnyComponent(scene.userData.componentIdentifiers, componentsToShow)
      ) {
        if (scene.userData.isHidden) {
          scene.userData.isHidden = false;
          promises.push(this.animateOpacity(scene.children, 0.2, 1));
        }
      }
    }
    return promises;
  }

  async updateSelectedArea({
    componentIdentifier,
    componentIdentifiers,
    mainGroupIdentifier,
    fallbackToDefault,
  }) {
    await this.animationRunning();

    if (mainGroupIdentifier) {
      this.mainGroupIdentifier = mainGroupIdentifier;
    }

    const groupName = GroupNameByMainGroupIdentifier[this.mainGroupIdentifier];

    if (!componentIdentifiers && componentIdentifier) {
      componentIdentifiers = [componentIdentifier];
    }

    let viewName = (
      componentIdentifiers
        ? componentIdentifiers
            .map((identifier) =>
              [groupName, 'front', 'middle', 'rear'].map(
                (groupName) => `component:${identifier}:${groupName}`
              )
            )
            .flat()
        : []
    )
      .concat([this.mainGroupIdentifier, 'default'])
      .find((name) => this.cameraViews[name]);

    if (fallbackToDefault && !this.cameraViews[viewName]) {
      viewName = 'default';
    }

    // jump to component view
    !this.surroundingsOn && this.jumpToView(viewName);

    if (!componentIdentifier && viewName) {
      // try to match the component identifier from the viewName
      const identifierMatches = viewName.match(/^[^:]+:(.+):[^:]+$/);
      if (identifierMatches) {
        componentIdentifier = identifierMatches[1];
      }
    }

    const componentsToHide = await this.getComponentsToHide(componentIdentifiers);
    const componentsToShow = this.lastHiddenComponents || [];

    // update hidden objects
    const promises = this.updateHiddenObjects(componentsToHide, componentsToShow);
    this.lastComponentIdentifier = componentIdentifier;
    this.lastHiddenComponents = componentsToHide;

    if (!promises.length) {
      this.render();
      return;
    }

    this.startAnimation('opacity');
    await Promise.all(promises);
    this.stopAnimation('opacity');
  }

  resetCurrentView(options = {}) {
    const { clearComponent } = options;
    const viewName = this.currentCameraView;
    this.currentCameraView = '';
    return !this.surroundingsOn && clearComponent
      ? this.updateSelectedArea(options)
      : this.jumpToView(viewName);
  }

  getObjectGroupName(object) {
    const matches = object.userData?.path?.match(/\/(front|middle|rear)[^/]*\//);
    return (matches && matches[1]) || this.getObjectGroupName(object.parent);
  }

  isMiddleCameraTarget(object) {
    return object
      ? Object.prototype.hasOwnProperty.call(object, 'isMiddleCameraTarget')
        ? object.isMiddleCameraTarget
        : this.isMiddleCameraTarget(object.parent)
      : false;
  }

  async initCameraViews(cameras) {
    await this.sceneLoaded;
    if (!this.controls) {
      return;
    }
    const box = this.getSceneContainerBox();
    const size = box.getSize(new Vector3());
    if (!size.length()) {
      return;
    }
    const center = new Vector3();
    box.getCenter(center);

    if (!this.surroundingsOn) {
      // skip default update if surroundings mode is active
      this.cameraViews.default = null;
    }
    this.cameraViews.surroundings = null;

    for (const camera of cameras) {
      const groupName = this.getObjectGroupName(camera);
      if (groupName === 'middle' && !this.isMiddleCameraTarget(camera)) {
        continue; // skip if not the middle segment
      }

      const dir = new Vector3();
      const position = new Vector3();
      camera.getWorldPosition(position);
      camera.getWorldDirection(dir);
      const lookat = dir.multiplyScalar(position.distanceTo(center)).add(position);

      const orientation = Object3D.DEFAULT_UP;
      const fov = camera.fov || 45;

      const cameraView = {
        position,
        lookat,
        orientation,
        fov,
        camera,
      };

      // map camera views to component names (with the objectName:useFor=component1,component2 name)
      // remove _Orientation suffix added by blender gltf exporter
      const matches = camera.userData?.name?.replace('_Orientation', '').match(/:useFor=(([^,]+,?)+)/);
      const componentIdentifiers = matches && matches[1] && matches[1].split(/\s*,\s*/);
      if (componentIdentifiers) {
        for (const componentIdentifier of componentIdentifiers) {
          this.cameraViews[`component:${componentIdentifier}:${groupName}`] = cameraView;
        }
      } else if (camera.userData?.name) {
        this.cameraViews[camera.userData.name] = cameraView;
      }
    }
    if (!this.cameraViews.default) {
      const position = center
        .clone()
        .add(this.defaultRelativeCameraPosition.clone().multiplyScalar(this.aspectFitDistance));
      this.cameraPosition.copy(position).normalize();
      this.cameraViews.default = {
        position,
        lookat: center.clone(),
        orientation: new Vector3(0, 1, 0),
        fov: 45,
        applyMinDistance: true,
      };
      if (!this.currentCameraView && !this.surroundingsOn) {
        this.jumpToView('default');
      }
    }
    if (!this.cameraViews.surroundings) {
      this.cameraPosition.copy(this.cameraViews.default.position).normalize();
      this.cameraViews.surroundings = {
        position: new Vector3(),
        lookat: center.clone(),
        orientation: new Vector3(0, 1, 0),
        applyMinDistance: true,
      };
    }
    this.screenshotCameraViews = {
      default: {
        ...this.cameraViews.default,
        calculatePosition: (distance = this.getFitDistance()) =>
          center.clone().add(this.relativeScreenshotPosition.clone().multiplyScalar(distance)),
      },
    };
  }

  jumpToView(viewName, animate = true, duration = {}) {
    if (this.currentCameraView === viewName) {
      return;
    }
    const view = this.cameraViews[viewName];
    if (!view) {
      return;
    }
    this.currentCameraView = viewName;

    const {
      position,
      orientation,
      lookat: target,
      fov,
    } = {
      ...this.cameraViews.default,
      ...view,
    };

    if (view.applyMinDistance) {
      this.controls.minDistance = this.minDistance;
    } else {
      this.controls.minDistance = 0;
    }

    if (animate) {
      this.moveCamera(position, orientation, target, fov, duration);
    } else {
      this.setCameraPosition(position, orientation, target, fov);
    }
  }

  moveCamera(position, orientation, target, fov, duration = {}) {
    super.moveCamera(position, orientation, target, duration);
    if (this.camera.fov !== fov) {
      new TWEEN.Tween(this.camera, this.cameraTweenGroup)
        .to({ fov }, 700)
        .easing(TWEEN.Easing.Quadratic.Out)
        .onUpdate(() => {
          this.camera.updateProjectionMatrix();
        })
        .start();
    }
  }

  setCameraPosition(position = {}, orientation = {}, target = {}, fov) {
    if (!this.camera) {
      throw new Error('The camera must be available for the setCameraPosition call!');
    }
    Object.assign(this.camera.position, position);
    Object.assign(this.camera.up, orientation);
    if (fov) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    if (this.controls && this.controls.target) {
      Object.assign(this.controls.target, target);
      if (!this.isAnimating()) {
        this.controls.update();
      }
    }
  }

  resetSceneIfItemParamsChanged(configuration) {
    const sceneHash = [
      configuration.item.identifier,
      configuration.customdata.width,
      configuration.customdata.length,
    ].join('+');
    // reset if sceneHash does not match
    if (this._sceneHash !== sceneHash) {
      this._sceneHash = sceneHash;
      this.cameraViews = {};
      this.hiddenObjects = [];
      this.currentCameraView = null;
      this.lastComponentIdentifier = null;
      this.loadItemParams(configuration.item.identifier);
    }
  }

  async update(configuration) {
    try {
      await this._surroundingsPromise;
    } catch (e) {
      // wait only
    }
    this.resetSceneIfItemParamsChanged(configuration);
    await super.update(configuration);
    this.updateDynamicParts(configuration);
    this.updateHiddenState();
    this.surroundingsOn && (await this.updateSurroundings(configuration, true));
    this.render();
  }

  updateHiddenState() {
    const hiddenComponents = this.lastHiddenComponents;
    for (const scene of this.sceneContainer.children) {
      if (
        this.componentIdentifierIncludesAnyComponent(scene.userData.componentIdentifiers, hiddenComponents)
      ) {
        scene.userData.isHidden = true;
        this.setOpacity(scene.children, 0.2);
      }
    }
  }

  loadItemParams(itemIdentifier) {
    const loader = new FileLoader().setResponseType('json');
    this.itemParamsPromise = new Promise((resolve, reject) =>
      loader.load(
        `${this._modelBasePath}/visualization_component/creator/${itemIdentifier}/params.json`,
        resolve,
        undefined,
        reject
      )
    );
    return this.itemParamsPromise;
  }

  onLoadFinished = () => null;

  onLoadStart = () => null;

  onLoadFinishedCustom = async () => {
    try {
      await this.sceneLoaded;
    } catch {
      // ignore
    }
    try {
      await this._surroundingsPromise;
    } catch {
      // ignore
    }
    this.observable.dispatch(EventTypes.LoadFinished, {
      screenshot: !!this._screenshottingInProgress,
    });
  };

  onLoadStartCustom = () => {
    this.observable.dispatch(EventTypes.LoadStart, {
      screenshot: !!this._screenshottingInProgress,
    });
  };

  getModelsForPosition(position) {
    return this.sceneContainer.children.filter((obj) => obj.userData.path?.includes(`/${position}/`));
  }

  updateModelsArr(models, updateFn) {
    models.forEach(updateFn);
  }

  updateDynamicParts(configuration) {
    if (!this.sceneContainer?.children?.length) {
      console.log('Scene is empty, skipping update of dynamic parts');
      return;
    }
    const segmentSize = 5;
    const minSize = 7.5;

    const totalLength = parseFloat(configuration.customdata.length) || 0;
    if (!totalLength) {
      return;
    }
    const steelBeltLength = Math.floor((totalLength - minSize) / segmentSize);

    // clean up clones
    this.sceneContainer?.children
      .filter((object) => object.isClone)
      .forEach((object) => object.parent.remove(object));

    const segmentArr = this.getModelsForPosition('middle');
    this.updateModelsArr(segmentArr, (model) => {
      model.visible = false;
    });
    const cameraTargetSegmentNumber = Math.floor(steelBeltLength / 2);
    for (let i = 0; i < steelBeltLength; i++) {
      this.updateModelsArr(segmentArr, (segment) => {
        const segmentCopy = segment.clone();
        segmentCopy.isClone = true;
        segmentCopy.visible = true;
        segmentCopy.position.z = -i * segmentSize;
        segmentCopy.isMiddleCameraTarget = i === cameraTargetSegmentNumber;
        this.sceneContainer.add(segmentCopy);
      });
    }
    const rearArr = this.getModelsForPosition('rear');
    this.updateModelsArr(rearArr, (model) => {
      model.position.z = -steelBeltLength * segmentSize;
    });

    if (this._steelBeltLength !== steelBeltLength) {
      this._steelBeltLength = steelBeltLength;
      this.currentCameraView = '';
    }

    this.centerContainer(this.sceneContainer);
    this.fitSceneToCamera();
    return this.initCameraViews(this.getCameras(this.sceneContainer));
  }

  centerContainer(container) {
    container.position.x = 0;
    container.position.z = 0;
    const center = new Box3().setFromObject(container).getCenter(new Vector3());
    container.position.x = -center.x;
    container.position.z = -center.z;
  }

  async toggleSurroundings(on) {
    const prevState = this.surroundingsOn;
    this.surroundingsOn = on;
    if (prevState === on) {
      try {
        return await this._surroundingsPromise;
      } catch (e) {
        // ignore
      }
    }
    try {
      await this._surroundingsPromise;
    } catch (e) {
      // ignore
    }
    this._surroundingsPromise = this._toggleSurroundings();
    return await this._surroundingsPromise;
  }

  async _toggleSurroundings() {
    if (this.surroundingsOn) {
      await this.sceneLoaded;

      if (!this._controlsOriginalValues) {
        // save original control values
        this._controlsOriginalValues = { ...this.controls };
      }

      try {
        await this.updateSurroundings(this._configuration);
      } catch (e) {
        console.error('Could not update surroundings', e);
      }

      if (!this.surroundings) {
        return;
      }

      this.jumpToView('surroundings');
      // fade in surroundings
      this.startAnimation('surroundings');
      await this.animateOpacity([this.surroundings], 0, 1);
      this.stopAnimation('surroundings');

      await this.animationRunning();
    } else if (this.surroundings && this._controlsOriginalValues) {
      this.jumpToView('default');
      // fade out surroundings then remove it
      this.startAnimation('surroundings');
      await this.animateOpacity([this.surroundings], 1, 0).then(() => this.cleanUpSurroundings());
      this.stopAnimation('surroundings');
      await this.animationRunning();
    }
  }

  cleanUpSurroundings() {
    this.surroundings && this.scene.remove(this.surroundings);
    this.surroundings = null;
  }

  async updateSurroundings(configuration, toggleIfNotPresent) {
    const {
      surrounding_type: type,
      surrounding_width: width,
      surrounding_length: length,
      surrounding_height: height,
    } = configuration.customdata;

    if (!(type && width && length && height)) {
      if (this.surroundings) {
        this.cleanUpSurroundings();
        if (this._controlsOriginalValues) {
          Object.assign(this.controls, this._controlsOriginalValues);
          delete this._controlsOriginalValues;
          this.jumpToView('default', false);
        }
      }
      this.surroundingsOn = false;
      return;
    }

    if (!this.surroundings && toggleIfNotPresent) {
      try {
        return await this.toggleSurroundings(true);
      } catch (e) {
        console.error('Could not toggle surroundings', e);
      }
      return;
    }

    const surroundings = await this.surroundingsBuilder.update({
      width,
      length,
      height,
      type: type?.replace(/^surrounding_/, ''),
      containerBox: this.getSceneContainerBox(),
    });

    this.cleanUpSurroundings();

    this.surroundings = surroundings;
    if (this.surroundings) {
      this.scene.add(this.surroundings);

      this.controls.maxDistance = Math.max(length, width) * 0.75;

      this.cameraViews.surroundings?.position.setFromSphericalCoords(
        this.controls.maxDistance,
        this.surroundingsControls.polarAngle,
        this.surroundingsControls.azimuthAngle
      );
    }

    return surroundings;
  }

  setupScreenshots() {
    if (this.surroundings) {
      this.surroundings.visible = false;
    }
    const { maxDistance } = this.controls;
    this.controls.maxDistance = Infinity;
    return () => {
      if (this.surroundings) {
        this.surroundings.visible = true;
      }
      this.controls.maxDistance = maxDistance;
    };
  }

  async getScreenshots(callback, { viewList, variantList, size }) {
    if (!this.renderer) {
      return callback({});
    }
    this._screenshottingInProgress = true;
    this.endAnimation();
    await this.animationRunning();
    const removeTempOverlay = await this.createTempOverlay();

    // store original state values
    const origVariantPath = this._materialVariantPath;
    const origPosition = this.camera.position.clone();
    const origOrientation = this.camera.up.clone();
    const origTarget = this.controls ? this.controls.target.clone() : false;
    const origSize = new Vector2();
    this.renderer.getSize(origSize);
    const origPixelRatio = this.renderer.getPixelRatio();
    const controlsValues = { ...this.controls };
    if (this._controlsOriginalValues) {
      Object.assign(this.controls, this._controlsOriginalValues);
    }

    const restoreOriginalValues = async () => {
      this._screenshottingInProgress = false;
      this.renderer.setSize(origSize.width, origSize.height);
      this.renderer.setPixelRatio(origPixelRatio);
      this.updateCamera(origSize.width, origSize.height);
      Object.assign(this.controls, controlsValues);
      this.setCameraPosition(origPosition, origOrientation, origTarget);
      this._materialVariantPath = origVariantPath;
      await this.updateMaterial();
      this.render();
      removeTempOverlay();
    };

    const restoreScreenshotSetup = await this.setupScreenshots();

    // get the size from the size param, or use default values
    const width = size?.width || 1024;
    const height = size?.height || 768;

    const screenshots = {};
    const views = this.screenshotCameraViews || this.cameraViews;
    // if the viewList option is provided, use it as keys instead of the list of all views
    const viewKeyList = viewList instanceof Array && viewList.length > 0 ? viewList : Object.keys(views);

    for (let variant of variantList) {
      if (this._abortScreenshotting) {
        return await restoreOriginalValues();
      }

      const data = {};
      this.setMaterialVariantPath(variant);

      try {
        await this.updateMaterial(this.scene, { screenshot: true });
      } catch (e) {
        // ignore errors
        continue;
      }

      for (let viewKey of viewKeyList) {
        if (this._abortScreenshotting) {
          return await restoreOriginalValues();
        }

        const view = views[viewKey];
        const {
          position,
          calculatePosition,
          orientation,
          lookat: target,
        } = {
          ...this.cameraViews.default,
          ...view,
        };
        // set the camera to the screenshot size
        this.renderer.setPixelRatio(1);
        this.renderer.setSize(width, height);
        this.updateCamera(width, height);
        this.camera.clearViewOffset();

        this.setCameraPosition(calculatePosition ? calculatePosition() : position, orientation, target);
        this.render();

        data[viewKey] = this.renderer.domElement.toDataURL('image/png');
      }
      screenshots[variant] = data;
    }

    await restoreScreenshotSetup();

    await restoreOriginalValues();

    callback(screenshots);
  }

  addCameraHelper(viewName) {
    const view = this.cameraViews[viewName];
    if (view?.camera) {
      const helper = new CameraHelper(view.camera);
      this.scene.add(helper);
      this.render();
    } else {
      console.error('view does not exist: ' + viewName);
    }
  }
}

export default Visualization;
